import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertTriangle, Save, X, CheckCircle } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';

interface UnsavedChangesModalProps {
  isOpen: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  onSave?: () => void;
  title?: string;
  message?: string;
  showSaveOption?: boolean;
}

const UnsavedChangesModal: React.FC<UnsavedChangesModalProps> = ({
  isOpen,
  onConfirm,
  onCancel,
  onSave,
  title = "Unsaved Changes",
  message = "You have unsaved scores that will be lost if you continue.",
  showSaveOption = false
}) => {
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop with stage-like blur */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9999]"
            onClick={onCancel}
          />
          
          {/* Modal */}
          <div className="fixed inset-0 flex items-center justify-center z-[10000] p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{ type: "spring", duration: 0.4, bounce: 0.1 }}
              className="relative bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl shadow-pink-500/15 shadow-2xl hover:shadow-2xl transition-shadow duration-300 w-full max-w-md"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Glassmorphism overlay */}
              <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent rounded-2xl" />
              
              {/* Content */}
              <div className="relative p-6">
                {/* Header with icon */}
                <div className="flex items-start gap-4 mb-6">
                  <div className="flex-shrink-0 p-3 rounded-full bg-gradient-to-r from-pink-500/20 to-purple-500/20 border border-pink-400/30">
                    <AlertTriangle className="w-6 h-6 text-pink-400" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-white mb-2">
                      {title}
                    </h3>
                    <p className="text-white/80 leading-relaxed">
                      {message}
                    </p>
                  </div>
                </div>

                {/* Action buttons */}
                <div className="flex flex-col sm:flex-row gap-2">
                  {/* Cancel Button (first - most common action) */}
                  <button
                    onClick={onCancel}
                    className="flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg font-medium transition-all duration-200 bg-white/10 border border-white/20 text-white/80 hover:bg-white/20 hover:border-white/30 hover:text-white hover:scale-[1.02] active:scale-[0.98] text-sm"
                  >
                    Cancel
                  </button>

                  {/* Discard & Continue Button */}
                  <button
                    onClick={onConfirm}
                    className="flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg font-medium transition-all duration-200 bg-red-500/20 border border-red-400/40 text-red-400 hover:bg-red-500/30 hover:text-red-300 hover:scale-[1.02] active:scale-[0.98] text-sm"
                  >
                    <X className="w-3 h-3" />
                    {showSaveOption ? 'Discard' : 'Continue'}
                  </button>

                  {/* Save & Continue Button (primary action when available) */}
                  {showSaveOption && onSave && (
                    <button
                      onClick={onSave}
                      className="flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg font-medium transition-all duration-200 bg-gradient-to-r from-pink-500 to-purple-600 text-white hover:from-pink-600 hover:to-purple-700 hover:scale-[1.02] active:scale-[0.98] shadow-lg hover:shadow-xl text-sm"
                    >
                      <CheckCircle className="w-3 h-3" />
                      Save & Go
                    </button>
                  )}
                </div>
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
};

export default UnsavedChangesModal;
